import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { ParamMap, Params } from '@angular/router'
import {
  ContainerPartial,
  EntityHubLayout,
  FilterableGridPartial,
  FilterablePartial,
  GridContainerPartial,
  ListPartial,
  MultipleSectionsViewModel
} from '@flocasts/experience-service-types'
import { combineLatest, Observable, of, shareReplay, switchMap } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { EXPERIENCE_API_EVENT_HUB_BASE_PATH, EXPERIENCE_API_VERSION } from 'src/app/app.config'
import { DataService } from 'src/app/data/data.service'
import { LOGGER_SERVICE } from 'src/app/logger/logger.config'
import { LoggerService } from 'src/app/logger/logger.interface'
import { CacheDataService } from 'src/app/singleton-services/cache-data.service'
import {
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT_VARIATION,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_VARIATION
} from '../../shared/experimentation/experiment.model'
import { ExperimentationService } from '../../shared/experimentation/experimentation.service'
import { SiteIds } from '../../shared/models/site.model'
import { VerticalService } from '../../singleton-services/vertical.service'
@Injectable({ providedIn: 'root' })
export class EventHubDataService {
  constructor(
    private readonly http: HttpClient,
    private readonly cacheDataService: CacheDataService,
    private readonly dataService: DataService,
    private readonly experimentationService: ExperimentationService,
    private readonly verticalService: VerticalService,
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService
  ) {}

  public readonly isNextGenEventHub$ = this.experimentationService.isInVariation(
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING,
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_VARIATION,
    false
  )
  public readonly isNextGenEventHubShort$ = this.experimentationService.isInVariation(
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT,
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT_VARIATION,
    false
  )
  public isNextGen$ = combineLatest([this.isNextGenEventHubShort$, this.isNextGenEventHub$]).pipe(
    switchMap(([nextGenShort, nextGen]) => {
      return of(nextGen || nextGenShort)
    }),
    shareReplay(1)
  )

  public readonly siteId$ = this.verticalService.siteId$

  // START: CALLS USED TO DRIVE SINGLE EVENT HUB RESULTS TAB IN LEGACY EVENT HUBS

  /**
   * Get results tab data for event hub.
   * @deprecated
   */
  public getEventHubResults(
    nodeId: number,
    params?: Params,
    paginatedUrl?: string
  ): Observable<ListPartial | MultipleSectionsViewModel> {
    const url = paginatedUrl ? paginatedUrl : `${EXPERIENCE_API_EVENT_HUB_BASE_PATH}/${nodeId}/results`
    let httpParams = new HttpParams({
      fromObject: { ...params }
    })

    if (!paginatedUrl) httpParams = httpParams.set('version', EXPERIENCE_API_VERSION)

    return this.getEventHubsEndpoint(url, httpParams)
  }

  /**
   * Get results list for event hub.
   * @deprecated
   */
  public getEventHubResultsList(nodeId: number, params?: Params): Observable<ListPartial | MultipleSectionsViewModel> {
    const url = `${EXPERIENCE_API_EVENT_HUB_BASE_PATH}/${nodeId}/results/list`
    const httpParams = new HttpParams({
      fromObject: {
        version: EXPERIENCE_API_VERSION,
        ...params
      }
    })

    return this.getEventHubsEndpoint(url, httpParams)
  }

  /**
   * @deprecated
   */

  private getEventHubsEndpoint(url: string, httpParams: HttpParams) {
    return this.http.get<ListPartial | MultipleSectionsViewModel>(url, { params: httpParams }).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 404) this.logger.error(`EventHubDataService error: ${url}`, error)

        return of(error.error as MultipleSectionsViewModel)
      })
    )
  }

  // END: CALLS USED TO DRIVE SINGLE EVENT HUB RESULTS TAB IN LEGACY EVENT HUBS

  public getEventHubLayoutWithCacheWrapper(
    id: number,
    tab: string | null | undefined,
    queryParams: ParamMap | null,
    shouldShowBrackets: boolean = false
  ): Observable<EntityHubLayout | undefined> {
    return combineLatest([this.isNextGen$, this.siteId$]).pipe(
      switchMap(([isNextGen, siteId]) => {
        let params = new HttpParams({
          fromObject: {
            version: EXPERIENCE_API_VERSION,
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'
          }
        })

        const facets = queryParams?.get('facets')
        params = facets ? params.set('facets', facets) : params
        const search = queryParams?.get('search')
        params = search ? params.set('search', search) : params
        const playing = queryParams?.get('playing')
        params = playing ? params.set('playing', playing) : params
        const tournamentId = queryParams?.get('tournamentId')
        const resultId = queryParams?.get('resultId')
        params = resultId ? params.set('resultId', resultId) : params
        params = tournamentId ? params.set('tournamentId', tournamentId) : params
        params = params.set('isEventHubLayoutEnabled', true)
        params = params.set('isEventHubBracketsEnabled', shouldShowBrackets)
        params = params.set('isNextGenEventHub', isNextGen)
        params = params.set('site_id', siteId || SiteIds.FLODOGS)

        const endpoint = `${EXPERIENCE_API_EVENT_HUB_BASE_PATH}/${id}${!!tab ? `/${tab}` : ''}`
        return this.cacheDataService.wrapper<EntityHubLayout>(endpoint, params, true).pipe(
          catchError(err => {
            this.logger.error(err.message, err.error)
            return of(undefined)
          })
        )
      })
    )
  }

  public getEventHubLayout(
    id: number,
    tab: string | null | undefined,
    queryParams: ParamMap | null,
    shouldShowBrackets: boolean = false
  ): Observable<EntityHubLayout | undefined> {
    return combineLatest([this.isNextGen$, this.siteId$]).pipe(
      switchMap(([isNextGen, siteId]) => {
        let params = new HttpParams({
          fromObject: {
            version: EXPERIENCE_API_VERSION,
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'
          }
        })

        const facets = queryParams?.get('facets')
        params = facets ? params.set('facets', facets) : params
        const tournamentId = queryParams?.get('tournamentId')
        params = tournamentId ? params.set('tournamentId', tournamentId) : params
        const resultId = queryParams?.get('resultId')
        params = resultId ? params.set('resultId', resultId) : params
        params = params.set('isEventHubLayoutEnabled', true)
        params = params.set('isEventHubBracketsEnabled', shouldShowBrackets)
        params = params.set('isNextGenEventHub', isNextGen)
        params = params.set('site_id', siteId || SiteIds.FLODOGS)

        const endpoint = `${EXPERIENCE_API_EVENT_HUB_BASE_PATH}/${id}${!!tab ? `/${tab}` : ''}`
        return this.http.get<EntityHubLayout>(endpoint, { params }).pipe(
          catchError(err => {
            this.logger.error(err.message, err.error)
            return of(undefined)
          })
        )
      })
    )
  }

  public getEventHubPartial(
    id: number,
    tab: string | null,
    queryParams: ParamMap | null,
    shouldShowBrackets: boolean = false
  ): Observable<
    | FilterablePartial
    | FilterableGridPartial
    | GridContainerPartial
    | MultipleSectionsViewModel
    | ContainerPartial
    | undefined
  > {
    if (!tab) return of(undefined)
    return combineLatest([this.isNextGen$, this.siteId$]).pipe(
      switchMap(([isNextGen, siteId]) => {
        let params = new HttpParams({
          fromObject: {
            version: EXPERIENCE_API_VERSION,
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'
          }
        })
        const facets = queryParams?.get('facets')
        params = facets ? params.set('facets', facets) : params
        const search = queryParams?.get('search')
        params = search ? params.set('search', search) : params
        const limit = queryParams?.get('limit')
        params = limit ? params.set('limit', limit) : params
        const offset = queryParams?.get('offset')
        params = offset ? params.set('offset', offset) : params
        const tournamentId = queryParams?.get('tournamentId')
        params = tournamentId ? params.set('tournamentId', tournamentId) : params
        const resultId = queryParams?.get('resultId')
        params = resultId ? params.set('resultId', resultId) : params
        params = params.set('isEventHubLayoutEnabled', true)
        params = params.set('isEventHubBracketsEnabled', shouldShowBrackets)
        params = params.set('isNextGenEventHub', isNextGen)
        params = params.set('site_id', siteId || SiteIds.FLODOGS)
        const endpoint = `${EXPERIENCE_API_EVENT_HUB_BASE_PATH}/${id}/${tab}/partial`

        return this.http
          .get<
            | FilterablePartial
            | FilterableGridPartial
            | GridContainerPartial
            | MultipleSectionsViewModel
            | ContainerPartial
          >(endpoint, {
            params
          })
          .pipe(
            catchError(err => {
              this.logger.error(err.message, err.error)
              return of(undefined)
            })
          )
      })
    )
  }

  public getEventHubPaginatedPartial(endpoint: string): Observable<FilterablePartial | undefined> {
    return this.dataService.get<FilterablePartial>(endpoint)
  }
}
